import { useChartColors } from '../lib/accessibility'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, CheckCircle2, Droplets, TrendingDown } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { api } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', bgRaised: 'var(--color-bg-card)', bgCard: '#1A2212',
  border: 'var(--color-border)', amber: 'var(--color-primary)', green: '#2D6A2F',
  text: 'var(--color-text)', textMuted: 'var(--color-text-muted)', textFaint: 'var(--color-text-faint)',
  red: '#F87171', blue: '#60A5FA',
  active: 'color-mix(in srgb, var(--color-primary) 12%, transparent)',
}

type Tab = 'wells' | 'usage'

interface Well {
  id: string; name: string; type: string; depth_m?: number
  pump_brand?: string; pump_hp?: number; last_tested_date?: string
  permit_number?: string; is_active: boolean; notes?: string
  gpm?: number
}

interface WaterUsage {
  id: string; well_id?: string; well_name?: string; date: string
  gallons: number; duration_minutes?: number; field_name?: string; notes?: string
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px',
  color: C.text, fontSize: 13, outline: 'none',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 500, color: C.textMuted, marginBottom: 5,
}

function AddWellModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ name: '', type: 'drilled', depth_m: '', pump_brand: '', pump_hp: '', last_tested_date: '', permit_number: '', is_active: true, notes: '' })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post('/wells', d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wells'] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate({ ...form, depth_m: form.depth_m ? parseFloat(form.depth_m) : undefined, pump_hp: form.pump_hp ? parseFloat(form.pump_hp) : undefined })
  }
  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 460, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Add Well</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div style={{ fontSize: 15, fontWeight: 600 }}>Well added!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 2 }}><label style={labelStyle}>Name *</label><input required style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. North Well" /></div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Type</label>
                <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
                  {['drilled','dug','artesian','bored','jetted'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Depth (m)</label><input type="number" step="0.1" style={inputStyle} value={form.depth_m} onChange={e => set('depth_m', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Pump Brand</label><input style={inputStyle} value={form.pump_brand} onChange={e => set('pump_brand', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Pump HP</label><input type="number" step="0.5" style={inputStyle} value={form.pump_hp} onChange={e => set('pump_hp', e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Last Tested</label><input type="date" style={inputStyle} value={form.last_tested_date} onChange={e => set('last_tested_date', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Permit #</label><input style={inputStyle} value={form.permit_number} onChange={e => set('permit_number', e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="is_active" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
              <label htmlFor="is_active" style={{ fontSize: 13, color: C.textMuted, cursor: 'pointer' }}>Well is active</label>
            </div>
            <div><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Add Well'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function LogUsageModal({ wells, onClose }: { wells: Well[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ well_id: wells[0]?.id ?? '', date: new Date().toISOString().slice(0, 10), gallons: '', duration_minutes: '', field_name: '', notes: '' })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post(`/wells/${form.well_id}/water-usage`, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['water-usage'] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate({ date: form.date, gallons: parseFloat(form.gallons), duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : undefined, field_name: form.field_name, notes: form.notes })
  }
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 420, maxWidth: '95vw', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Log Water Usage</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div>Usage logged!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>Well</label>
              <select style={inputStyle} value={form.well_id} onChange={e => set('well_id', e.target.value)}>
                {wells.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={form.date} onChange={e => set('date', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Gallons *</label><input required type="number" style={inputStyle} value={form.gallons} onChange={e => set('gallons', e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Duration (min)</label><input type="number" style={inputStyle} value={form.duration_minutes} onChange={e => set('duration_minutes', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Field / Zone</label><input style={inputStyle} value={form.field_name} onChange={e => set('field_name', e.target.value)} /></div>
            </div>
            <div><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 48, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Log Usage'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function buildMonthlyUsage(usage: WaterUsage[]) {
  const now = new Date()
  const months: { label: string; gallons: number }[] = []
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    months.push({ label: d.toLocaleDateString([], { month: 'short', year: '2-digit' }), gallons: 0 })
  }
  usage.forEach(u => {
    const d = new Date(u.date)
    const key = d.toLocaleDateString([], { month: 'short', year: '2-digit' })
    const slot = months.find(m => m.label === key)
    if (slot) slot.gallons += u.gallons
  })
  return months
}

export function WaterPage() {
  const chartC = useChartColors()
  const [tab, setTab] = useState<Tab>('wells')
  const [showAddWell, setShowAddWell] = useState(false)
  const [showLogUsage, setShowLogUsage] = useState(false)

  const { data: wells = [], isLoading: wellsLoading, isError: wellsError, refetch: refetchWells } = useQuery<Well[]>({
    queryKey: ['wells'],
    queryFn: () => api.get('/wells').then(r => r.data?.wells ?? r.data ?? []),
  })

  const { data: usageLog = [], isLoading: usageLoading } = useQuery<WaterUsage[]>({
    queryKey: ['water-usage'],
    queryFn: () => api.get('/wells/water-usage').then(r => r.data?.usage_logs ?? r.data ?? []).catch(() => []),
    enabled: tab === 'usage',
  })

  const monthlyUsage = buildMonthlyUsage(usageLog)
  const activeWells = wells.filter(w => w.is_active).length
  const totalGallonsThisMonth = usageLog.filter(u => {
    const d = new Date(u.date)
    const now = new Date()
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
  }).reduce((sum, u) => sum + u.gallons, 0)

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '7px 16px', borderRadius: 7, fontSize: 13, fontWeight: active ? 600 : 400,
    cursor: 'pointer', background: active ? C.active : 'transparent',
    color: active ? C.amber : C.textMuted, border: 'none',
  })

  return (
    <>
      {showAddWell && <AddWellModal onClose={() => setShowAddWell(false)} />}
      {showLogUsage && <LogUsageModal wells={wells} onClose={() => setShowLogUsage(false)} />}

      <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Water & Wells</h1>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textMuted }}>Well inventory and water usage tracking</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowLogUsage(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 14, cursor: 'pointer' }}>
              Log Usage
            </button>
            <button onClick={() => setShowAddWell(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              <Plus size={16} /> Add Well
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total Gallons This Month', value: totalGallonsThisMonth.toLocaleString(), color: C.blue },
            { label: 'Active Wells', value: activeWells, color: '#4ADE80' },
            { label: 'Total Wells', value: wells.length, color: C.textMuted },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontSize: 11, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: C.bgCard, borderRadius: 10, padding: 4, border: `1px solid ${C.border}`, width: 'fit-content' }}>
          <button style={tabStyle(tab === 'wells')} onClick={() => setTab('wells')}>Wells</button>
          <button style={tabStyle(tab === 'usage')} onClick={() => setTab('usage')}>Usage Log</button>
        </div>

        {/* Wells Tab */}
        {tab === 'wells' && (
          wellsLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: C.textFaint }}>Loading wells…</div>
          ) : wellsError ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <p style={{ color: C.red, marginBottom: 12 }}>Failed to load wells</p>
              <button onClick={() => refetchWells()} style={{ padding: '8px 16px', borderRadius: 8, background: C.bgCard, border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer' }}>Retry</button>
            </div>
          ) : wells.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Droplets size={40} style={{ color: C.textFaint, opacity: 0.4, marginBottom: 12 }} />
              <p style={{ margin: 0, color: C.textFaint }}>No wells added yet</p>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: C.textFaint }}>Click "Add Well" to start tracking your water sources</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {wells.map(well => (
                <div key={well.id} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Droplets size={16} style={{ color: C.blue, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{well.name}</div>
                        <div style={{ fontSize: 12, color: C.textMuted }}>{well.type} well</div>
                      </div>
                    </div>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5, background: well.is_active ? '#4ADE8022' : 'var(--color-bg-hover)', color: well.is_active ? '#4ADE80' : C.textFaint, fontWeight: 600 }}>
                      {well.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {well.depth_m && <div><div style={{ fontSize: 11, color: C.textFaint }}>Depth</div><div style={{ fontSize: 13, color: C.text }}>{well.depth_m}m</div></div>}
                    {well.pump_brand && <div><div style={{ fontSize: 11, color: C.textFaint }}>Pump</div><div style={{ fontSize: 13, color: C.text }}>{well.pump_brand} {well.pump_hp ? `${well.pump_hp}HP` : ''}</div></div>}
                    {well.gpm && <div><div style={{ fontSize: 11, color: C.textFaint }}>Flow</div><div style={{ fontSize: 13, color: C.text }}>{well.gpm} GPM</div></div>}
                    {well.last_tested_date && <div><div style={{ fontSize: 11, color: C.textFaint }}>Last Tested</div><div style={{ fontSize: 13, color: C.text }}>{new Date(well.last_tested_date).toLocaleDateString()}</div></div>}
                    {well.permit_number && <div><div style={{ fontSize: 11, color: C.textFaint }}>Permit #</div><div style={{ fontSize: 13, color: C.text }}>{well.permit_number}</div></div>}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Usage Log Tab */}
        {tab === 'usage' && (
          <>
          {usageLog.length > 0 && (
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <TrendingDown size={14} style={{ color: C.blue }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Monthly Usage — Last 6 Months</span>
              </div>
              <ResponsiveContainer width="100%" height={130}>
                <BarChart data={monthlyUsage} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fill: chartC.faint, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: chartC.faint, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : String(v)} width={40} />
                  <Tooltip contentStyle={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, color: C.text }} formatter={(v: number) => [`${v.toLocaleString()} gal`, 'Usage']} labelStyle={{ color: C.textMuted }} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                  <Bar dataKey="gallons" radius={[4, 4, 0, 0]} maxBarSize={36}>
                    {monthlyUsage.map((_, i) => <Cell key={i} fill={i === monthlyUsage.length - 1 ? C.blue : C.blue + '66'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Water Usage Log</span>
              <button onClick={() => setShowLogUsage(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 7, background: C.green, border: 'none', color: '#fff', fontSize: 13, cursor: 'pointer' }}><Plus size={14} /> Log Usage</button>
            </div>
            {usageLoading ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: C.textFaint }}>Loading…</div>
            ) : usageLog.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: C.textFaint }}>No usage recorded yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Date','Well','Gallons','Duration','Field'].map(h => <th key={h} style={{ padding: '10px 18px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {usageLog.map(u => (
                    <tr key={u.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '12px 18px', color: C.textMuted }}>{new Date(u.date).toLocaleDateString()}</td>
                      <td style={{ padding: '12px 18px', color: C.text, fontWeight: 500 }}>{u.well_name ?? '—'}</td>
                      <td style={{ padding: '12px 18px', color: C.text }}>{u.gallons.toLocaleString()} gal</td>
                      <td style={{ padding: '12px 18px', color: C.textMuted }}>{u.duration_minutes ? `${u.duration_minutes} min` : '—'}</td>
                      <td style={{ padding: '12px 18px', color: C.textMuted }}>{u.field_name ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          </>
        )}
      </main>
    </>
  )
}
